import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import users from "../../apiConfigs/users";
import { ReactComponent as Property } from "../../assets/FilledProperty.svg";
import { ReactComponent as User } from "../../assets/FilledUserIcon.svg";
import CardTab from "../../designComponents/CardTab";
import Dropdown from "../../designComponents/Dropdown/Dropdown";
import Typography from "../../designComponents/Typography";
import { Marketplace, Trade } from "../../layout/Sidebar/icons";
import requestAPI from "../../utils/requestAPI";
import { ButtonsSection, Title, Wrapper } from "../Properties/Details/styles";
import Charts from "./Charts";

export const CardWrapper = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(1, 1fr);

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

interface AnalyticsType {
  properties: {
    totalProperties: number;
    totalBookings: number;
    totalContracts: number;
    activeProperties: number;
    draftProperties: number;
    underReviewProperties: number;
  };
  trades: {
    totalActiveTrades: number;
    totalDraftTrades: number;
    totalTradesUnderContract: number;
    totalTradesClosed: number;
    totalQuotes: number;
  };
  marketplace: {
    totalDraftProducts: number;
    totalActiveProducts: number;
    totalSoldProducts: number;
    totalExpiredProducts: number;
  };
  users: {
    totalLandlords: number;
    totalTenants: number;
    totalServiceProviders: number;
    totalUsers: number;
  };
}

interface GrowthAnalytics {
  last7Days: MonthlyData[];
  last15Days: MonthlyData[];
  thisMonth: MonthlyData[];
  selectedMonth: MonthlyData[];
}
interface MonthlyData {
  date: string;
  status: string;
  count?: number;
  users: number;
  properties: number;
  day: number;
}
const generateYearsFromStart = (startYear: number) => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = startYear; year <= currentYear + 10; year++) {
    years.push({ label: `${year}`, value: `${year}` });
  }

  return years;
};

const Dashboard = () => {
  const navigate = useNavigate();

  const [analytics, setAnalytics] = useState<AnalyticsType>();
  const [growthAnalytics, setGrowthAnalytics] = useState<GrowthAnalytics>();
  const [growthPropertyAnalytics, setGrowthPropertyAnalytics] =
    useState<GrowthAnalytics>();

  const [filterUser, setFilterUser] = useState("last7Days");
  const [filterProperty, setFilterProperty] = useState("last7Days");

  const [customDateUsers, setCustomDateUsers] = useState({
    month: 1,
    year: new Date().getFullYear(),
  });

  const [customDateProperty, setCustomDateProperty] = useState({
    month: 1,
    year: new Date().getFullYear(),
  });

  const getDashViewAnalytics = async () => {
    try {
      await requestAPI(users.getAnalytics()).then((res) => setAnalytics(res));
    } catch (error) {
      console.log(error);
    }
  };
  const getGraphAnalytics = async () => {
    try {
      await requestAPI(
        users.getGrowthAnalytics(
          filterUser === "custom" ? customDateUsers : undefined
        )
      ).then((res) => setGrowthAnalytics(res));

      await requestAPI(
        users.getPropertyAnalytics(
          filterProperty === "custom" ? customDateProperty : undefined
        )
      ).then((res) => setGrowthPropertyAnalytics(res));
    } catch (error) {
      console.log(error);
    }
  };
  //FOR NOW ONLY QUICK FIX AND VIEW OF ITEM
  // const handleNavigate = (type: string) => {
  //   if (type === "totalUser") {
  //     navigate(`/users`);
  //   }
  //   if (type === "totalTenants") {
  //     navigate(`/users?role=TENANT`);
  //   }
  //   if (type === "totalLandlords") {
  //     navigate(`/users?role=LANDLORD`);
  //   }
  //   if (type === "totalProperties") {
  //     navigate(`/properties`);
  //   }
  //   if (type === "activeProperties") {
  //     navigate(`/properties?status=ACTIVE`);
  //   }
  //   if (type === "rejectedProperties") {
  //     navigate(`/properties?status=REJECTED`);
  //   }
  //   if (type === "activeProperties") {
  //     navigate(`/properties?status=ACTIVE`);
  //   }
  //   if (type === "draftProperties") {
  //     navigate(`/properties?status=DRAFT`);
  //   }
  //   if (type === "underReviewProperties") {
  //     navigate(`/properties?status=UNDER_REVIEW`);
  //   }
  //   if (type === "totalBooking") {
  //     navigate(`/bookings`);
  //   }
  // };
  useEffect(() => {
    getDashViewAnalytics();
  }, []);

  useEffect(() => {
    getGraphAnalytics();
  }, [customDateUsers, customDateProperty]);

  const propertiesCardData = [
    {
      title: "Total Properties",

      count: analytics?.properties?.totalProperties || 0,
      icon: Property,
      value: "totalProperties",
    },
    {
      title: "Total Bookings",
      count: analytics?.properties?.totalBookings || 0,
      icon: Property,
      value: "totalBookings",
    },
    {
      title: "Total Contracts",
      count: analytics?.properties?.totalContracts || 0,
      icon: Property,
      value: "totalContracts",
    },
    {
      title: "Draft Properties",

      count: analytics?.properties?.draftProperties || 0,
      icon: Property,
      value: "draftProperties",
    },
    {
      title: "Active Properties",

      count: analytics?.properties?.activeProperties || 0,
      icon: Property,
      value: "activeProperties",
    },

    {
      title: "Properties Under Review",

      count: analytics?.properties?.underReviewProperties || 0,
      icon: Property,
      value: "underReviewProperties",
    },
  ];

  const usersCardData = [
    {
      title: "Total Users",
      count: analytics?.users?.totalUsers || 0,
      icon: User,
      value: "totalUser",
    },
    {
      title: "Total Tenants",

      count: analytics?.users?.totalTenants || 0,
      icon: User,
      value: "totalTenants",
    },
    {
      title: "Total Landlords",
      count: analytics?.users?.totalLandlords || 0,
      icon: User,
      value: "totalLandlords",
    },
    {
      title: "Total Service Providers",
      count: analytics?.users?.totalServiceProviders || 0,
      icon: User,
      value: "totalServiceProviders",
    },
  ];
  const tradesCardData = [
    {
      title: "Total Active Trades",
      count: analytics?.trades?.totalActiveTrades || 0,
      icon: Trade,
      value: "totalActiveTrades",
    },
    {
      title: "Total Drafts",
      count: analytics?.trades?.totalDraftTrades || 0,
      icon: Trade,
      value: "draftTrades",
    },
    {
      title: "Total Quotes",
      count: analytics?.trades?.totalQuotes || 0,
      icon: Trade,
      value: "totalQuotes",
    },
    {
      title: "Total Trades Closed",
      count: analytics?.trades?.totalTradesClosed || 0,
      icon: Trade,
      value: "totalTradesClosed",
    },
    {
      title: "Total Trades UnderContract",
      count: analytics?.trades?.totalTradesUnderContract || 0,
      icon: Trade,
      value: "tradesUnderContract",
    },
  ];
  const marketplaceCardData = [
    {
      title: "Total Active Products",
      count: analytics?.marketplace?.totalActiveProducts || 0,
      icon: Marketplace,
      value: "totalActiveProducts",
    },
    {
      title: "Total Draft Products",
      count: analytics?.marketplace?.totalDraftProducts || 0,
      icon: Marketplace,
      value: "totalDraftProducts",
    },
    {
      title: "Total Expired Products",
      count: analytics?.marketplace?.totalExpiredProducts || 0,
      icon: Marketplace,
      value: "totalExpiredProducts",
    },
    {
      title: "Total Sold Products",
      count: analytics?.marketplace?.totalSoldProducts || 0,
      icon: Marketplace,
      value: "totalSoldProducts",
    },
  ];
  return (
    <Wrapper style={{ backgroundColor: "#f9f9fd", padding: 26 }}>
      <Title>Dashboard</Title>

      <div style={{ display: "flex", marginBottom: "60px", gap: 30 }}>
        {growthAnalytics && (
          <div
            style={{
              display: "flex",
              width: "100%",
              flexDirection: "column",
            }}
          >
            <ButtonsSection>
              <Typography
                variant="h4"
                size="h4"
                style={{ marginLeft: "10px" }}
                fontWeight="medium"
              >
                User Growth
              </Typography>
              <div style={{ display: "flex", gap: 20 }}>
                {filterUser === "custom" && (
                  <Dropdown
                    style={{
                      container: { paddingTop: 10 },
                      label: { paddingBottom: 6, color: "#555A77" },
                      select: {
                        backgroundColor: "#F1EFFB",
                        fontSize: 14,
                        fontFamily: "urbanist",
                        fontWeight: 500,
                        padding: "8px",
                      },
                    }}
                    options={[
                      { label: "Jan", value: "01" },
                      { label: "Feb", value: "02" },
                      { label: "Mar", value: "03" },
                      { label: "Apr", value: "04" },
                      { label: "May", value: "05" },
                      { label: "Jun", value: "06" },
                      { label: "Jul", value: "07" },
                      { label: "Aug", value: "08" },
                      { label: "Sep", value: "09" },
                      { label: "Oct", value: "10" },
                      { label: "Nov", value: "11" },
                      { label: "Dec", value: "12" },
                    ]}
                    onChange={(e) => {
                      setCustomDateUsers({
                        month: Number(e.target.value),
                        year: customDateUsers.year,
                      });
                    }}
                  />
                )}
                {filterUser === "custom" && (
                  <Dropdown
                    style={{
                      container: { paddingTop: 10 },
                      label: { paddingBottom: 6, color: "#555A77" },
                      select: {
                        backgroundColor: "#F1EFFB",
                        fontSize: 14,
                        fontFamily: "urbanist",
                        fontWeight: 500,
                        padding: "8px",
                      },
                    }}
                    options={generateYearsFromStart(2010)}
                    onChange={(e) => {
                      setCustomDateUsers({
                        month: customDateUsers.month,
                        year: Number(e.target.value),
                      });
                    }}
                  />
                )}
                <Dropdown
                  style={{
                    container: { paddingTop: 10 },
                    label: { paddingBottom: 6, color: "#555A77" },
                    select: {
                      backgroundColor: "#F1EFFB",
                      fontSize: 14,
                      fontFamily: "urbanist",
                      fontWeight: 500,
                      padding: "8px",
                    },
                  }}
                  value={filterUser}
                  options={[
                    { label: "7 Days", value: "last7Days" },
                    { label: "15 Days", value: "last15Days" },
                    { label: "This Month", value: "thisMonth" },
                    { label: "Custom Date", value: "custom" },
                  ]}
                  onChange={(e) => {
                    setFilterUser(e.target.value);
                  }}
                />
              </div>
            </ButtonsSection>
            <div style={{ width: "100%" }}>
              <Charts
                type="user"
                data={
                  filterUser === "last7Days"
                    ? growthAnalytics.last7Days
                    : filterUser === "last15Days"
                    ? growthAnalytics.last15Days
                    : filterUser === "thisMonth"
                    ? growthAnalytics.thisMonth
                    : growthAnalytics.selectedMonth
                }
              />
            </div>
          </div>
        )}
        {growthPropertyAnalytics && (
          <div
            style={{
              display: "flex",
              width: "100%",
              flexDirection: "column",
            }}
          >
            <ButtonsSection>
              <Typography
                variant="h4"
                size="h4"
                style={{ marginLeft: "10px" }}
                fontWeight="medium"
              >
                Property Growth
              </Typography>
              <div style={{ display: "flex", gap: 20 }}>
                {filterProperty === "custom" && (
                  <Dropdown
                    style={{
                      container: { paddingTop: 10 },
                      label: { paddingBottom: 6, color: "#555A77" },
                      select: {
                        backgroundColor: "#F1EFFB",
                        fontSize: 14,
                        fontFamily: "urbanist",
                        fontWeight: 500,
                        padding: "8px",
                      },
                    }}
                    options={[
                      { label: "Jan", value: "01" },
                      { label: "Feb", value: "02" },
                      { label: "Mar", value: "03" },
                      { label: "Apr", value: "04" },
                      { label: "May", value: "05" },
                      { label: "Jun", value: "06" },
                      { label: "Jul", value: "07" },
                      { label: "Aug", value: "08" },
                      { label: "Sep", value: "09" },
                      { label: "Oct", value: "10" },
                      { label: "Nov", value: "11" },
                      { label: "Dec", value: "12" },
                    ]}
                    onChange={(e) => {
                      setCustomDateProperty({
                        month: Number(e.target.value),
                        year: customDateUsers.year,
                      });
                    }}
                  />
                )}
                {filterProperty === "custom" && (
                  <Dropdown
                    style={{
                      container: { paddingTop: 10 },
                      label: { paddingBottom: 6, color: "#555A77" },
                      select: {
                        backgroundColor: "#F1EFFB",
                        fontSize: 14,
                        fontFamily: "urbanist",
                        fontWeight: 500,
                        padding: "8px",
                      },
                    }}
                    options={generateYearsFromStart(2010)}
                    onChange={(e) => {
                      setCustomDateProperty({
                        month: customDateUsers.month,
                        year: Number(e.target.value),
                      });
                    }}
                  />
                )}
                <Dropdown
                  style={{
                    container: { paddingTop: 10 },
                    label: { paddingBottom: 6, color: "#555A77" },
                    select: {
                      backgroundColor: "#F1EFFB",
                      fontSize: 14,
                      fontFamily: "urbanist",
                      fontWeight: 500,
                      padding: "8px",
                    },
                  }}
                  value={filterProperty}
                  options={[
                    { label: "7 Days", value: "last7Days" },
                    { label: "15 Days", value: "last15Days" },
                    { label: "This Month", value: "thisMonth" },
                    { label: "Custom Date", value: "custom" },
                  ]}
                  onChange={(e) => {
                    setFilterProperty(e.target.value);
                  }}
                />
              </div>
            </ButtonsSection>
            <div style={{ width: "100%" }}>
              <Charts
                type="property"
                data={
                  filterProperty === "last7Days"
                    ? growthPropertyAnalytics.last7Days
                    : filterProperty === "last15Days"
                    ? growthPropertyAnalytics.last15Days
                    : filterProperty === "thisMonth"
                    ? growthPropertyAnalytics.thisMonth
                    : growthPropertyAnalytics.selectedMonth
                }
              />
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Typography variant="h4" size="h4" fontWeight="medium">
          Users
        </Typography>
        <div
          style={{
            display: "grid",
            gap: "10px",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          }}
        >
          {usersCardData.map((data, idx) => (
            <React.Fragment key={idx}>
              <CardTab
                enableBorder
                img={data.icon}
                title={data.title}
                count={data?.count}
                key={idx}
                onClick={() => {
                  // handleNavigate(data.value);
                }}
              />
            </React.Fragment>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Typography variant="h4" size="h4" fontWeight="medium">
          Properties
        </Typography>
        <CardWrapper>
          {propertiesCardData.map((data, idx) => (
            <React.Fragment key={idx}>
              <CardTab
                enableBorder
                img={data.icon}
                title={data.title}
                count={data?.count}
                key={idx}
                onClick={() => {
                  // handleNavigate(data.value);
                }}
              />
            </React.Fragment>
          ))}
        </CardWrapper>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Typography variant="h4" size="h4" fontWeight="medium">
          Trades
        </Typography>
        <CardWrapper>
          {tradesCardData.map((data, idx) => (
            <React.Fragment key={idx}>
              <CardTab
                enableBorder
                img={data.icon}
                title={data.title}
                count={data?.count}
                key={idx}
                onClick={() => {
                  // handleNavigate(data.value);
                }}
              />
            </React.Fragment>
          ))}
        </CardWrapper>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Typography variant="h4" size="h4" fontWeight="medium">
          Marketplace Products
        </Typography>
        <CardWrapper>
          {marketplaceCardData.map((data, idx) => (
            <React.Fragment key={idx}>
              <CardTab
                enableBorder
                img={data.icon}
                title={data.title}
                count={data?.count}
                key={idx}
                onClick={() => {
                  // handleNavigate(data.value);
                }}
              />
            </React.Fragment>
          ))}
        </CardWrapper>
      </div>
    </Wrapper>
  );
};

export default Dashboard;
